/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { DatePicker } from '@atlaskit/datetime-picker';
import { Field } from '@atlaskit/form';
import { Box } from '@atlaskit/primitives/compiled';
import { mergeStyles } from '@atlaskit/select';
import type { FocusAskTargetDateValue } from '@atlassian/conversation-assistant-message-actions-api/focus-ask';

import { parseStringToIsoDay } from '../../format-focus-ask-target-date';
import messages from '../../messages';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function toPickerValue(raw: string): string | undefined {
	const trimmed = raw.trim();
	if (!trimmed) {
		return undefined;
	}
	return ISO_DATE.test(trimmed) ? trimmed : undefined;
}

/** Local calendar day as YYYY-MM-DD (DatePicker `minDate` / `maxDate` contract). */
function formatLocalIsoDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${y}-${m}-${day}`;
}

const styles = cssMap({
	fullWidth: {
		width: '100%',
	},
});

type Props = {
	value: FocusAskTargetDateValue;
	onChange: (value: FocusAskTargetDateValue) => void;
	hideLabel?: boolean;
	isCompact?: boolean;
	fieldId?: string;
	isDisabled?: boolean;
	appearance?: 'default' | 'subtle';
};

export const TargetDateField = ({
	value,
	onChange,
	hideLabel = false,
	isCompact = false,
	fieldId = 'focus-ask-target-date-field',
	isDisabled = false,
	appearance,
}: Props): JSX.Element => {
	const { formatMessage, locale } = useIntl();
	const labelText = formatMessage(messages.targetDateFieldLabel);

	const datePicker = (
		<Box xcss={styles.fullWidth}>
			<DatePicker
				id={`${fieldId}-input`}
				testId={fieldId}
				locale={locale}
				minDate={formatLocalIsoDate(new Date())}
				value={toPickerValue(value.targetDate)}
				onChange={(v) =>
					onChange(
						v ? { targetDate: parseStringToIsoDay(v), targetDateType: 'DAY' } : { targetDate: '' },
					)
				}
				clearControlLabel={formatMessage(messages.targetDatePickerClear)}
				shouldShowCalendarButton
				label={labelText}
				inputLabel={labelText}
				openCalendarLabel={formatMessage(messages.targetDatePickerOpenCalendar)}
				spacing={isCompact ? 'compact' : 'default'}
				isDisabled={isDisabled}
				appearance={appearance}
				selectProps={{
					styles: mergeStyles({
						container: (provided) => ({
							...provided,
							width: '100%',
						}),
					}),
				}}
			/>
		</Box>
	);

	if (hideLabel) {
		return datePicker;
	}

	return (
		<Field name="targetDate" label={labelText}>
			{() => datePicker}
		</Field>
	);
};
